import {
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Modal,
  Search,
  VSpacer,
} from '@/components/DesignSystem';
import { useResizeObserver } from '@/hooks/useResizeObserver';
import { ApiErpCategory } from '@api/interfaces/ApiErpCategory';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Divider, Stack } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

interface ErpCategorySelectionModalProps {
  disabledIds?: string[],
  erpCategories: ApiErpCategory[],
  onChange: (selectedIds: string[]) => void,
  onClose: () => void,
  selectedIds: string[],
}

export const ErpCategorySelectionModal = ({
  disabledIds = [],
  erpCategories,
  onChange,
  onClose,
  selectedIds,
}: ErpCategorySelectionModalProps) => {
  const [search, setSearch] = useState('');
  const [expandedCategoryMap, setExpandedCategoryMap] = useState<Record<string, boolean>>({});
  const modalRef = React.useRef<HTMLDivElement>(null);
  const height = useResizeObserver(modalRef);

  const handleCategorySelect = (id: string) => {
    const category = erpCategories.find((category) => category.id === id)!;
    const isParent = !category.parentId;
    const isCurrentlySelected = isChecked(category);
    if (isParent && category.subcategories?.length) {
      const subcategoryIds = category.subcategories.map((sub) => sub.id) ?? [];
      if (isCurrentlySelected) {
        onChange(selectedIds.filter((x) => !subcategoryIds.includes(x)));
      } else {
        const newSelectedIds = new Set([...selectedIds, ...subcategoryIds]);
        onChange(Array.from(newSelectedIds));
      }
    } else {
      if (isCurrentlySelected) {
        onChange(selectedIds.filter((x) => x !== id));
      } else {
        onChange([...selectedIds, id]);
      }
    }
  };

  const isChecked = (category: ApiErpCategory): boolean => {
    const isParentCategory = !category.parentId;
    if (isParentCategory && category.subcategories?.length) {
      const subcategoryIds = category.subcategories.map((sub) => sub.id);
      return subcategoryIds.every((id) => selectedIds.includes(id));
    } else {
      return selectedIds.includes(category.id);
    }
  };

  const isDisabled = (category: ApiErpCategory): boolean => {
    const isParentCategory = !category.parentId;
    if (isParentCategory && category.subcategories?.length) {
      const subcategoryIds = category.subcategories.map((sub) => sub.id);
      return subcategoryIds.every((id) => disabledIds.includes(id));
    } else {
      return disabledIds.includes(category.id);
    }
  };

  const isIndeterminate = (category: ApiErpCategory): boolean => {
    if (category.parentId || !category.subcategories?.length) {
      return false;
    }
    const subcategoryIds = category.subcategories.map((sub) => sub.id);
    const hasSomeSubcategoriesSelected = selectedIds.some((id) => subcategoryIds.includes(id));
    const hasAllSubcategoriesSelected = subcategoryIds.every((id) => selectedIds.includes(id));
    return hasSomeSubcategoriesSelected && !hasAllSubcategoriesSelected;
  };

  const visibleRows = useMemo(() => {
    const filteredParents = erpCategories.filter(({ parentId, name, subcategories }) => (
      !parentId
      && (
        name.toLowerCase().includes(search.toLowerCase())
        || subcategories?.some((subcategory) => (
          subcategory.name.toLowerCase().includes(search.toLowerCase())
        ))
      )
    ));

    type Row = {
      category: ApiErpCategory,
      type: 'parent' | 'sub',
    };
    const result: Row[] = [];

    filteredParents
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((parent) => {
        result.push({ category: parent, type: 'parent' });

        if (expandedCategoryMap[parent.id] && !!parent.subcategories?.length) {
          result.push(...parent.subcategories
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((subcategory => ({
              category: subcategory,
              type: 'sub' as Row['type'],
            }))),
          );
        }
      });

    return result;
  }, [erpCategories, search, expandedCategoryMap]);

  const Row = ({ index, style }: ListChildComponentProps) => {
    const item = visibleRows[index];
    const category = item.category;

    return (
      <Box style={style}>
        {item.type === 'parent' && (
          <Divider light />
        )}
        <Stack
          alignItems="center"
          direction="row"
          height="44px"
          justifyContent="space-between"
          key={category.id}
          pl={item.type === 'sub' ? 4 : 0}
        >
          <MenuItem
            disableRipple
            disabled={isDisabled(category)}
            key={category.id}
            onClick={() => handleCategorySelect(category.id)}
            testID={`erp-category-${category.id}`}
          >
            <Checkbox
              checked={isChecked(category)}
              indeterminate={isIndeterminate(category)}
              testID={`erp-category-${category.id}-checkbox`}
            >
              {category.name}
            </Checkbox>
          </MenuItem>
          {item.type === 'parent' && !!category.subcategories?.length && (
            <IconButton
              onClick={() =>
                setExpandedCategoryMap((prev) => ({
                  ...prev,
                  [category.id]: !prev[category.id],
                }))
              }
              testID={`erp-category-${category.id}-expand-button`}
            >
              {expandedCategoryMap[category.id] ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Stack>
        {index === visibleRows.length - 1 && (
          <Divider light />
        )}
      </Box>
    );
  };

  return (
    <Modal
      containerStyleOverride={{ minHeight: 0, height: '100%', overflow: 'hidden' }}
      footerSpacerSize="4"
      headerAccessoryRight={(
        <Button
          disabled={!selectedIds.length}
          onClick={() => onChange([])}
          startIcon={<DeleteOutline />}
          testID="clear-all-button"
          variant="text"
        >
          Clear all
        </Button>
      )}
      onClose={onClose}
      open
      testID="erp-category-selection-modal"
      title="Select categories"
      width={560}
    >
      <Box display="flex" flexDirection="column" ref={modalRef} sx={{ height: '100%' }}>
        <Search
          fullWidth
          onChangeText={setSearch}
          testID="erp-category-search"
          value={search}
        />
        <VSpacer size="7" />
        <FixedSizeList
          height={height - 199}
          itemCount={visibleRows.length}
          itemSize={44}
          width="100%"
        >
          {Row}
        </FixedSizeList>
        <Divider light sx={{ ml: '-32px', width: 560 }} />
        <VSpacer size="5" />
        <Stack alignItems="end">
          <Button
            onClick={onClose}
            testID="done-button"
            variant="contained"
          >
            Done
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};
